import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import theme, { mobileBreakpoint } from '../theme';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  will-change: opacity;
`;

const PopupContainer = styled(motion.div)`
  background: linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%);
  border-radius: ${props => props.theme.borderRadius.large};
  padding: 40px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(205, 255, 155, 0.3);
  position: relative;
  will-change: opacity, transform;
  border: 2px solid ${props => props.theme.colors.accent1};
`;

const CloseButton = styled.button`
  z-index: 1000;
  position: absolute;
  top: 20px;
  right: 20px;
  background: linear-gradient(135deg, ${props => props.theme.colors.secondary} 0%, #e8316d 100%);
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(219, 39, 98, 0.3);

  &:hover {
    background: linear-gradient(135deg, #e8316d 0%, ${props => props.theme.colors.secondary} 100%);
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(219, 39, 98, 0.4);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const PopupTitle = styled.h2`
  font-size: 24px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.secondary} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 20px;
  text-align: center;
  font-weight: 700;
  position: relative;
`;

const PopupContent = styled.div`
  text-align: center;
  color: ${props => props.theme.colors.primary};
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 30px;

    &::before {
    content: '';
    position: absolute;
    top: 73px;
    left: 50px;
    right: 50px;
    height: 1px;
    background: linear-gradient(90deg, transparent 0%, ${props => props.theme.colors.accent1} 50%, transparent 100%);
  }
`;

const ConfirmationPopupTextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 12px;
  border: 2px solid ${props => props.theme.colors.accent1};
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  margin-top: 16px;
  background: white;
  
  &::placeholder {
    color: #999;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  justify-content: ${props => props.showTextArea ? 'space-between' : 'center'};
  flex-wrap: wrap;
  width: 100%;
`;

const ConfirmButton = styled.button`
  padding: 12px 24px;
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: white;
  border: 2px solid #dc3545;
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 195px;


  &:hover {
    background: linear-gradient(135deg, #c82333 0%, #dc3545 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(220, 53, 69, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 520px) {
    width: 100%;
  }
`;

const CancelButton = styled.button`
  padding: 12px 24px;
  background: white;
  color: ${props => props.theme.colors.primary};
  border: 2px solid ${props => props.theme.colors.accent1};
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 195px;

  &:hover {
    background: ${props => props.theme.colors.accent1};
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 520px) {
    width: 100%;
  }
`;

const WarningIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  text-align: center;
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: 12px;
  margin: 16px 0;
  text-align: center;
  font-size: 14px;
  line-height: 1.4;
`;

const ConfirmationPopup = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title = "Bestätigung", 
  message = "Sind Sie sicher?", 
  confirmText = "Bestätigen", 
  cancelText = "Abbrechen",
  isDestructive = false,
  isLoading = false,
  showTextArea = false,
  prefilledText = "",
  onTextChange = null,
  placeholder,
  isConfirmDisabled = false,
  errorMessage = ""
}) => {
  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          onClick={onClose}
        >
          <PopupContainer
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
          >
            <CloseButton onClick={onClose}>
              ×
            </CloseButton>
            <PopupTitle>{title}</PopupTitle>
            <PopupContent>
              {isDestructive && (
                <WarningIcon>⚠️</WarningIcon>
              )}
              {message}
              {errorMessage && (
                <ErrorMessage>{errorMessage}</ErrorMessage>
              )}
              {showTextArea && (
                <ConfirmationPopupTextArea
                  placeholder={placeholder}
                  value={prefilledText}
                  onChange={(e) => onTextChange && onTextChange(e.target.value)}
                />
              )}
            </PopupContent>
            <ButtonGroup showTextArea={showTextArea}>
              <CancelButton onClick={onClose} disabled={isLoading}>
                {cancelText}
              </CancelButton>
              <ConfirmButton 
                onClick={handleConfirm} 
                disabled={isLoading || isConfirmDisabled}
                style={isDestructive ? {} : {
                  background: 'linear-gradient(135deg, #28a745 0%,rgb(27, 172, 61) 100%)',
                  borderColor: '#28a745',
                  boxShadow: '0 6px 20px rgba(40, 167, 69, 0.4)'
                }}
              >
                {isLoading ? 'Lädt...' : confirmText}
              </ConfirmButton>
            </ButtonGroup>
          </PopupContainer>
        </Overlay>
      )}
    </AnimatePresence>
  );
};

export default ConfirmationPopup; 